<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import gvc from '../../variables/gift-vouchers';

export default {
  name: 'GiftVoucherEmailList',
  data() {
    return {
      domain: undefined,
      products: [],
      countries: [],
      radioGroup: 'accepted',
      radioGroupAccepted: gvc.OPTION_STATUS_ALL,
      notificationText: undefined,
      notification: false,
      pagination: {
        rowsPerPage: 25,
        page: 1,
        sortBy: 'arrivalDate',
      },
      headers: [
        { text: 'Booking Ref', value: 'bookingRef' },
        { text: 'Product', value: 'product' },
        { text: 'Country', value: 'country' },
        { text: 'Arrival', value: 'arrivalDate' },
        { text: 'Status', value: 'status.date', width: '350px' },
      ],
      radioOptions: ['processing', 'sent', 'accepted', 'handled'],
      radioOptionsAccepted: { all: 'All', 1: 'Option 1', 2: 'Option 2', 3: 'Option 3' },
    };
  },
  mounted() {
    if (this.domains.items.length === 0 && !this.domains.loading) {
      this.loadDomains();
    }
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: this.$t('menu.gift-voucher.title'), to: '', disabled: true },
        { text: this.$t('menu.gift-voucher.pages.email-list.title'), to: this.$route.path },
      ],
      title: this.$t('menu.gift-voucher.pages.email-list.title'),
    });
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      loadDomains: 'giftVoucher/loadDomains',
      searchEmails: 'giftVoucher/searchEmailList',
    }),
    search() {
      if (!this.validSearchInput) {
        return;
      }
      this.resetRadioGroupAcceptedSelection();
      this.searchEmails(this.domain)
        .then(() => {
          if (this.bookings.error) {
            this.notificationText = this.$t('gift-voucher.emails.error');
            this.notification = true;
          } else if (this.bookings.items.length === 0) {
            this.notificationText = this.$t('gift-voucher.emails.noContent');
            this.notification = true;
          }
        });
    },
    resetRadioGroupAcceptedSelection() {
      this.radioGroupAccepted = gvc.OPTION_STATUS_ALL;
    },
    getTwentyBookingUrl(bookingId) {
      return `${process.env.TWENTY_FRONT_PAGE}/booking/bookings/${bookingId}/info`;
    },
    daysToArrival(date) {
      return this.$moment(date, 'Y-MM-DD')
        .diff(this.$moment(), 'days');
    },
    getDateTime(status, date) {
      if (!date) {
        return '';
      }
      const datetime = this.$moment.utc(date)
        .local()
        .format('Y-MM-DD HH:mm:ss');
      const prefix = (status === 'handled' ? `${this.$t('gift-voucher.emails.status.accepted')} ` : '');
      return `${prefix}${datetime}`;
    },
  },
  computed: {
    ...mapState({
      domains: state => state.giftVoucher.domains,
      bookings: state => state.giftVoucher.emailList.bookings,
    }),
    ...mapGetters({
      computedDomains: 'giftVoucher/getComputedDomains',
    }),
    computedBookings() {
      return this.bookings.items.filter((booking) => {
        if (this.products.length > 0 && !this.products.includes(booking.productTypeName)) {
          return false;
        }
        if (this.countries.length > 0 && !this.countries.includes(booking.countryName)) {
          return false;
        }
        return true;
      });
    },
    computedStatusBookings() {
      return this.computedBookings.filter((booking) => {
        let valid = booking.status.status === this.radioGroup;
        if ((this.radioGroup === 'accepted' || this.radioGroup === 'handled')
          && this.radioGroupAccepted !== gvc.OPTION_STATUS_ALL
          && +this.radioGroupAccepted !== booking.status.option) {
          valid = false;
        }
        return valid;
      });
    },
    computedProducts() {
      if (this.bookings.items.length === 0) {
        return [];
      }
      return this.bookings.items.map(booking => booking.productTypeName);
    },
    computedCountries() {
      if (this.bookings.items.length === 0) {
        return [];
      }
      return this.bookings.items.map(booking => booking.countryName);
    },
    validSearchInput() {
      return !!this.domain;
    },
    emailCount() {
      const counts = { processing: 0, sent: 0, accepted: 0, handled: 0 };
      const optionCounts = { all: 0, 1: 0, 2: 0, 3: 0 };
      let optionsFound = false;
      this.computedBookings.forEach((booking) => {
        counts[booking.status.status] += 1;
        if (booking.status.status === this.radioGroup) {
          optionCounts[gvc.OPTION_STATUS_ALL] += 1;
          if (booking.status.option) {
            optionsFound = true;
            optionCounts[booking.status.option] += 1;
          }
        }
      });
      return { counts, optionCounts, optionsFound };
    },
  },
};
</script>
<template>
  <v-flex>
    <v-container grid-list>
      <v-layout row wrap>
        <v-flex xs6 pr-5>
          <h2 class="mb-4 text-xs-center">{{ $t('gift-voucher.search') }}</h2>
          <v-select
            v-model="domain"
            :items="computedDomains"
            :loading="domains.loading"
            :disabled="domains.loading || bookings.loading"
            :label="$t('gift-voucher.domains.label')"
            item-value="id"
            item-text="name"
            dense
          />
          <v-btn
            @click="search"
            :disabled="!validSearchInput || bookings.loading"
            color="info"
            class="gift-voucher-emails__btn"
          >
            {{ $t('gift-voucher.search') }}
          </v-btn>
        </v-flex>

        <v-flex xs6 pl-5 v-if="bookings.items.length > 0">
          <h2 class="mb-4 text-xs-center">{{ $t('gift-voucher.filter') }}</h2>
          <v-select
            v-model="products"
            :items="computedProducts"
            :label="$t('gift-voucher.products.label')"
            dense
            multiple
          />
          <v-select
            v-model="countries"
            :items="computedCountries"
            :label="$t('gift-voucher.countries.label')"
            dense
            multiple
          />
          <v-layout row wrap>
            <v-flex xs6>
              <v-radio-group v-model="radioGroup">
                <v-radio
                  v-for="option in radioOptions"
                  :key="option"
                  :label="`${option} (${emailCount.counts[option]})`"
                  :value="option"
                  mandatory
                  row
                />
              </v-radio-group>
            </v-flex>
            <v-flex xs6
                    v-if="(radioGroup === 'accepted' || radioGroup === 'handled')
                    && emailCount.optionsFound"
            >
              <v-radio-group v-model="radioGroupAccepted">
                <v-radio
                  v-for="(option, index) in radioOptionsAccepted"
                  :key="index"
                  :label="`${option} (${emailCount.optionCounts[index]})`"
                  :value="index"
                  mandatory
                  row
                />
              </v-radio-group>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <v-container grid-list>
      <v-layout row>
        <v-flex xs12>
          <v-data-table
            :headers="headers"
            :pagination.sync="pagination"
            :items="computedStatusBookings"
            :loading="bookings.loading"
          >
            <template v-slot:items="props">
              <tr>
                <td>
                  <a :href="getTwentyBookingUrl(props.item.bookingId)" target="_blank">
                    {{ props.item.bookingRef }}
                  </a>
                </td>
                <td>{{ props.item.productTypeName }}</td>
                <td>{{ props.item.countryName }}</td>
                <td>
                  {{ props.item.arrivalDate | moment('DD/MM/YY') }}
                  <span class="grey--text text--darken-1 pl-1">
                    ({{ daysToArrival(props.item.arrivalDate) }})
                  </span>
                </td>
                <td>
                  <span>
                    {{ $t(`gift-voucher.emails.status.${props.item.status.status}`) }}
                    <template v-if="props.item.status.option">
                      ({{ props.item.status.option }})
                    </template>
                    <span class="grey--text text--darken-1 font-italic">
                      ({{ getDateTime(props.item.status.status, props.item.status.date) }})
                    </span>
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>
    <v-snackbar v-model="notification">{{ notificationText }}</v-snackbar>
  </v-flex>
</template>
<style lang="scss">
  .gift-voucher-emails {
    &__btn {
      float: right;
      margin-right: 0 !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/gift-vouchers/GiftVoucherEmailList.vue